import { useTheme } from '@emotion/react';
import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMedia } from 'react-use';

import type { WEB_FEATURES } from '@didaskill/domain/configurations/constants';
import { STATISTICS_TILES_TYPES, STATISTICS_TYPES } from '@didaskill/domain/reporting/constants';
import type { StudentStatistics } from '@didaskill/domain/reporting/types';
import type { StudentAchievement } from '@didaskill/domain/users/types';
import type { AvatarShape } from '@didaskill/types/web';
import { TilesList, Typography } from '@didaskill/ui/design-system';
import { formattedDurationMessages } from '@didaskill/ui/utils';
import {
  PARSE_DURATION_BASE_UNITS,
  PARSE_DURATION_OUTPUT_UNITS,
  parseDuration,
} from '@didaskill/utils/misc';

import { AchievementsCard } from '../../achievements-card/AchievementsCard';
import {
  STATISTICS_ICON_MAP,
  STATISTICS_TILES_TYPES_MAP,
  STATISTICS_TILES_TYPES_VARIANT_MAP,
  STATISTICS_TYPES_OBJECT_MAP,
} from './constants';
import messages, {
  statisticsTileTitleMessages,
  statisticsTileTypesMessages,
} from './Statistics.messages';
import * as styles from './Statistics.styles';

type StatisticsProps = {
  user: AvatarShape;
  achievements?: StudentAchievement[] | null;
  statistics?: StudentStatistics;
  isAchievementsLoading?: boolean;
  isStatisticsLoading?: boolean;
  withAchievements?: boolean;
  forbiddenStatisticsItems?: STATISTICS_TYPES[];
  forbiddenStatisticsFeatures?: WEB_FEATURES[];
};

export const Statistics = ({
  user,
  statistics,
  isStatisticsLoading,
  achievements,
  isAchievementsLoading,
  withAchievements = false,
  forbiddenStatisticsItems = [],
  forbiddenStatisticsFeatures = [],
}: StatisticsProps) => {
  const intl = useIntl();

  const theme = useTheme();

  const isAboveLarge = useMedia(`(min-width: ${theme.breakpoints.large})`);

  const totalSessionsDurationSubtitle = useMemo(() => {
    if (statistics?.totalSessionsDuration) {
      const parsedTime = parseDuration(
        statistics.totalSessionsDuration,
        PARSE_DURATION_BASE_UNITS.MINUTE,
        {
          outputUnits: [PARSE_DURATION_OUTPUT_UNITS.HOURS, PARSE_DURATION_OUTPUT_UNITS.MINUTES],
        },
      );
      return intl.formatMessage(formattedDurationMessages.ALL, {
        days: parsedTime.days,
        hours: parsedTime.hours,
        minutes: parsedTime.minutes,
        seconds: parsedTime.seconds,
      });
    }

    return '';
  }, [statistics?.totalSessionsDuration, intl]);

  const getStatisticsTileProps = useCallback(
    (statisticsType: STATISTICS_TYPES) => {
      const statisticsFieldName = STATISTICS_TYPES_OBJECT_MAP[statisticsType];
      const statisticsValue = statistics?.[statisticsFieldName];
      const isStatisticsRatio = typeof statisticsValue === 'object' && statisticsValue !== null;
      const count = isStatisticsRatio ? statisticsValue?.count : (statisticsValue as number);
      const subtitleValue = isStatisticsRatio ? `${count}/${statisticsValue.total}` : count;

      return {
        icon: STATISTICS_ICON_MAP[statisticsType],
        title: intl.formatMessage(statisticsTileTitleMessages[statisticsType]),
        subtitle:
          statisticsType === STATISTICS_TYPES.TOTAL_SESSIONS_DURATION
            ? totalSessionsDurationSubtitle
            : intl.formatMessage(messages.TILE_SUBTITLE, {
                value: subtitleValue,
              }),
        variant: STATISTICS_TILES_TYPES_VARIANT_MAP[STATISTICS_TILES_TYPES_MAP[statisticsType]],
        isLoading: isStatisticsLoading,
        isDisabled: !statistics,
      };
    },
    [totalSessionsDurationSubtitle, statistics, isStatisticsLoading, intl],
  );

  return (
    <div css={styles.container}>
      {Object.keys(STATISTICS_TILES_TYPES)
        .filter(
          (statisticTile) =>
            !forbiddenStatisticsFeatures.includes(statisticTile.toLowerCase() as WEB_FEATURES),
        )
        .map((currentType, index) => (
          <div key={currentType}>
            <div key={currentType} css={styles.item}>
              <TilesList>
                <TilesList.Title
                  title={<FormattedMessage {...statisticsTileTypesMessages[currentType]} />}
                />
                <TilesList.Group
                  isLoading={isStatisticsLoading}
                  tiles={Object.values(STATISTICS_TYPES)
                    .filter(
                      (statisticsType) =>
                        STATISTICS_TILES_TYPES_MAP[statisticsType] === currentType,
                    )
                    .filter((statisticsType) => !forbiddenStatisticsItems.includes(statisticsType))
                    .map((statisticsType) => getStatisticsTileProps(statisticsType))}
                />
              </TilesList>
            </div>

            {index === 0 && withAchievements && !isAboveLarge && (
              <div css={styles.item} className="medal-item">
                <Typography variant={Typography.VARIANTS.HEADING_4}>
                  <FormattedMessage {...messages.ACHIEVEMENTS} />
                </Typography>

                <AchievementsCard
                  achievements={achievements}
                  isLoading={isAchievementsLoading}
                  user={user}
                />
              </div>
            )}
          </div>
        ))}
    </div>
  );
};
